import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { VariantCard } from "@/components/draft/VariantCard/VariantCard";
import { toPlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import type { DraftState } from "@/components/ui/StatusPill/StatusPill";
import type { MediaSpec } from "@/components/ui/MediaPreview/MediaPreview";
import { AppShell } from "@/components/app/AppShell";
import { Field } from "@/components/auth/FormShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useCreateDraftJob,
  useDrafts,
  useJob,
  type DraftProvider,
  type DraftRecord,
} from "@/lib/queries";
import { CONTENT_KINDS, PLATFORMS } from "@notamen/core/types";
import type { ContentKind, Platform } from "@notamen/core/types";
import { GenerateMediaPanel } from "./GenerateMediaPanel";
import { GeneratePostPanel } from "./GeneratePostPanel";
import styles from "./GenerateStudio.module.css";
import mediaStyles from "./GenerateMedia.module.css";

type StudioMode = "drafts" | "media" | "post";

const KIND_LABEL: Record<ContentKind, string> = {
  caption: "Caption",
  carousel: "Carousel",
  script: "Video script",
  image_brief: "Image brief",
  hashtags: "Hashtags",
  hook_options: "Hook options",
};
const PROVIDER_LABEL: Record<DraftProvider, string> = {
  bedrock: "Bedrock",
  anthropic: "Anthropic API",
};

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export function GenerateStudio() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const qc = useQueryClient();
  const brandQuery = useBrand(ws, brand);
  const createJob = useCreateDraftJob(ws, brand);
  const [jobId, setJobId] = useState<string | undefined>();
  const jobQuery = useJob(ws, brand, jobId);
  const draftsQuery = useDrafts(ws, brand);

  // The worker writes drafts asynchronously, so the drafts list fetched
  // at mount is stale once a job completes. Refetch it the moment the
  // job reaches "done" — without this, a successful job renders as
  // "0 drafts produced".
  const jobStatus = jobQuery.data?.job.status;
  useEffect(() => {
    if (jobStatus === "done") {
      void qc.invalidateQueries({ queryKey: ["drafts", ws, brand] });
    }
  }, [jobStatus, ws, brand, qc]);

  const [mode, setMode] = useState<StudioMode>("drafts");
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [platforms, setPlatforms] = useState<Platform[]>(["instagram"]);
  const [kinds, setKinds] = useState<ContentKind[]>(["caption"]);
  const [providers, setProviders] = useState<DraftProvider[]>(["bedrock"]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/generate`} replace />;
  }

  const job = jobQuery.data?.job;
  const running = job?.status === "queued" || job?.status === "running";
  const canSubmit =
    !!text.trim() && platforms.length > 0 && kinds.length > 0 && providers.length > 0 && !running;

  async function onGenerate() {
    if (!canSubmit) return;
    const res = await createJob.mutateAsync({
      input: { text: text.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) },
      platforms,
      kinds,
      providers,
    });
    setJobId(res.jobId);
  }

  // Drafts produced by the active job, grouped by provider for comparison.
  const jobDrafts: DraftRecord[] = (draftsQuery.data?.drafts ?? []).filter(
    (d) => job?.draftIds.includes(d.draft.id),
  );
  // Group by what the job actually requested (job.providers), NOT the
  // live chip selection — the user may have toggled chips after submit.
  const byProvider = (job?.providers ?? []).map((p) => ({
    provider: p,
    drafts: jobDrafts.filter((d) => d.provider === p),
  }));

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
          Back to brand
        </Btn>
        <header className={styles.head}>
          <p className={styles.kicker}>Generate</p>
          <h1 className={styles.title}>
            Studio · {brandQuery.data?.brand.name ?? "…"}
          </h1>
          <p className={styles.sub}>
            {mode === "drafts"
              ? "Describe what to post about. We’ll draft it in your brand voice. Pick more than one engine to compare quality side by side."
              : mode === "post"
                ? "Write one brief and generate complete posts — matching captions and images together. Pick the pair you like and send it to your queue as a draft."
                : "Generate real photos and videos for your posts — feature your synced products and reference your uploaded media."}
          </p>
        </header>

        <div className={mediaStyles.modeSwitch} role="tablist" aria-label="Studio mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "drafts"}
            className={`${mediaStyles.modeBtn} ${mode === "drafts" ? mediaStyles.modeBtnOn : ""}`}
            onClick={() => setMode("drafts")}
          >
            <Icon name="edit" size={13} /> Text drafts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "post"}
            className={`${mediaStyles.modeBtn} ${mode === "post" ? mediaStyles.modeBtnOn : ""}`}
            onClick={() => setMode("post")}
          >
            <Icon name="sparkles" size={13} /> Whole post
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "media"}
            className={`${mediaStyles.modeBtn} ${mode === "media" ? mediaStyles.modeBtnOn : ""}`}
            onClick={() => setMode("media")}
          >
            <Icon name="image" size={13} /> Photo / Video
          </button>
        </div>

        {mode === "post" && ws && brand && (
          <GeneratePostPanel ws={ws} brand={brand} />
        )}

        {mode === "media" && (
          <GenerateMediaPanel ws={ws} brand={brand} platform={platforms[0] ?? "instagram"} />
        )}

        {mode === "drafts" && (
        <div className={styles.layout}>
          {/* Request form */}
          <div className={styles.form}>
            <Field label="What's this post about?" htmlFor="gen-text">
              <Textarea
                id="gen-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's the post about? A product drop, a sale, a behind-the-scenes moment…"
                rows={4}
              />
            </Field>
            <Field label="Extra notes (optional)" htmlFor="gen-notes" hint="Angle, must-mention details, tone nudges.">
              <Textarea
                id="gen-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Angle, must-mention details, tone nudges."
                rows={2}
              />
            </Field>

            <Field label="Platforms">
              <div className={styles.chips}>
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.chip} ${platforms.includes(p) ? styles.chipOn : ""}`}
                    onClick={() => setPlatforms((s) => toggle(s, p))}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Content kinds">
              <div className={styles.chips}>
                {CONTENT_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`${styles.chip} ${kinds.includes(k) ? styles.chipOn : ""}`}
                    onClick={() => setKinds((s) => toggle(s, k))}
                  >
                    {KIND_LABEL[k]}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Engines" hint="Pick both to compare Bedrock vs the Anthropic API.">
              <div className={styles.chips}>
                {(["bedrock", "anthropic"] as DraftProvider[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.chip} ${providers.includes(p) ? styles.chipOn : ""}`}
                    onClick={() => setProviders((s) => toggle(s, p))}
                  >
                    {PROVIDER_LABEL[p]}
                  </button>
                ))}
              </div>
            </Field>

            {createJob.error && <p className={styles.error}>{createJob.error.message}</p>}

            <Btn
              variant="primary"
              full
              size="lg"
              onClick={onGenerate}
              disabled={!canSubmit}
              loading={createJob.isPending || running}
              iconRight={<Icon name="zap" size={14} />}
            >
              {running ? "Generating…" : "Generate drafts"}
            </Btn>
          </div>

          {/* Results */}
          <div className={styles.results}>
            {!jobId && (
              <div className={styles.placeholder}>
                <Icon name="zap" size={28} color="var(--text-4)" />
                <p>Your drafts will appear here, grouped by engine.</p>
              </div>
            )}

            {jobId && jobQuery.isError && (
              <div className={styles.failed}>
                <Icon name="info" size={16} color="var(--danger)" />
                <div>
                  <strong>Lost track of that job.</strong>
                  <p>{jobQuery.error?.message ?? "Couldn't load the job status."} Try generating again.</p>
                </div>
              </div>
            )}

            {job && running && (
              <div className={styles.status}>
                <span className={styles.spinner} aria-hidden="true" />
                <span>
                  {job.status === "queued" ? "Queued…" : "Writing your drafts…"} This can take
                  up to a minute per engine.
                </span>
              </div>
            )}

            {job?.status === "failed" && (
              <div className={styles.failed}>
                <Icon name="info" size={16} color="var(--danger)" />
                <div>
                  <strong>Generation failed.</strong>
                  <p>{job.error ?? "No drafts were produced."}</p>
                </div>
              </div>
            )}

            {job?.status === "done" &&
              byProvider.map(({ provider, drafts }) => (
                <section key={provider} className={styles.providerGroup}>
                  <h2 className={styles.providerTitle}>
                    {PROVIDER_LABEL[provider]}
                    <span className={styles.providerCount}>{drafts.length} drafts</span>
                  </h2>
                  {drafts.length === 0 && (
                    <p className={styles.muted}>This engine produced nothing (check the job error).</p>
                  )}
                  {drafts.map((d) => (
                    <DraftCard key={d.draft.id} draft={d} />
                  ))}
                </section>
              ))}
          </div>
        </div>
        )}
      </div>
    </AppShell>
  );
}

const KIND_TEXT: Record<string, string> = {
  caption: "Caption",
  carousel: "Carousel",
  script: "Video script",
  image_brief: "Image brief",
  hashtags: "Hashtags",
  hook_options: "Hook options",
};

function DraftCard({ draft }: { draft: DraftRecord }) {
  const variants = draft.draft.variants;
  // Primary content variant (one with a real body) drives the card; any
  // hashtags across variants are collected.
  const primary =
    variants.find((v) => v.kind !== "hashtags" && v.kind !== "hook_options") ?? variants[0];
  const hashtags = variants.flatMap((v) => v.hashtags ?? []);
  const platform = toPlatformKey(draft.platform);

  // Real visual spec from generation (carousel slides / video frame /
  // image brief), falling back to any variant that carries one.
  const media: MediaSpec | undefined =
    primary?.media ?? variants.find((v) => v.media)?.media;

  const warnings = draft.draft.warnings.map((w) => ({
    level: (w.level === "block" ? "block" : "warn") as "block" | "warn",
    text: `${w.rule}: ${w.detail}`,
  }));

  const kindLabel = primary
    ? KIND_TEXT[primary.kind] ?? primary.kind
    : "Draft";

  return (
    <VariantCard
      platform={platform}
      kind={kindLabel}
      status={draft.status as DraftState}
      {...(primary?.hook ? { hook: primary.hook } : {})}
      body={primary?.body ?? ""}
      hashtags={hashtags}
      warnings={warnings}
      meta={draft.provider}
      {...(media ? { media } : {})}
    />
  );
}
